import { type CommunityDetail, type Image, hasChangeRequestStatus } from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { canManageCommunityProfile, usePermissions } from 'permissions';
import {
  Avatar,
  Button,
  Col,
  Delete2LineIcon,
  DotSeparator,
  ExitLineIcon,
  HeaderImage,
  type MenuProps,
  Modal,
  NotificationLineIcon,
  Row,
  Text,
  Title,
} from 'ui';
import { handleAxiosError } from 'ui';
import { images } from 'common/utils';
import { useUser } from 'features/auth';
import { ConsentsModal } from 'features/consents';

import { useCommunityQuery, useJoinCommunity, useLeaveCommunity, useUpdateCommunity } from '../../queries';
import { EditImageModal } from '../edit-image-modal';
import { NotificationSettingsModal } from '../notification-settings-modal';
import { RequestJoinModal } from '../request-join-modal';
import { type CommunityTabItem, CommunityTabs } from './community-tabs';
import { type JoinButtonStatus, JoinStatusButton } from './join-status-button';
import { mapCommunityTabToPathName } from './utilities';

type ModalOption = 'consents' | 'cover' | 'notifications' | 'request';

type P = {
  community: CommunityDetail;
  tab: CommunityTabItem;
};

const CommunityHeader = ({ community, tab }: P) => {
  const { railsUserData: viewer } = useUser();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpened, setIsOpened] = useState<ModalOption>();

  const [modal, contextHolder] = Modal.useModal();

  const { canLeaveCommunity, canViewCommunityMembers, hasMembershipInCommunity } = usePermissions();

  const isMemberInCommunity = hasMembershipInCommunity({ communityId: community.id });
  const isRequestPending = hasChangeRequestStatus({ community, status: 'pending' });

  const { isRefetching: isReloadingCommunity } = useCommunityQuery({ communityId: community.id });

  const {
    error: joinError,
    isPending: isJoiningCommunity,
    isSuccess: isJoinedSuccess,
    mutate: joinCommunity,
  } = useJoinCommunity();

  const { error: leaveError, isPending: isLeavingCommunity, mutate: leaveCommunity } = useLeaveCommunity();

  const {
    error: updateError,
    isPending: isUpdatingCommunity,
    isSuccess: isCommunityUpdated,
    mutate: updateCommunity,
  } = useUpdateCommunity();

  useEffect(() => {
    handleAxiosError(joinError);
  }, [joinError]);

  useEffect(() => {
    handleAxiosError(leaveError);
  }, [leaveError]);

  useEffect(() => {
    handleAxiosError(updateError);
  }, [updateError]);

  useEffect(() => {
    if (isCommunityUpdated) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO: fix later
      setIsOpened(undefined);
    }
  }, [isCommunityUpdated]);

  const onRequestJoin = useCallback(
    (reason?: string) => {
      setIsOpened(undefined);
      joinCommunity({ id: community.id, privacy: community.privacy, reason });
    },
    [community.id, community.privacy, joinCommunity],
  );

  const onEditCoverImage = useCallback(
    (image: Image) => {
      updateCommunity({ data: { image }, id: community.id });
    },
    [community.id, updateCommunity],
  );

  const communityInfoItems = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      [community.organization?.categories[0]?.name, t('membersCount', { count: community.membersCount })]
        .filter(Boolean)
        .map((text, index) => ({ content: <Text>{text}</Text>, key: `community-info-item-${index}` })),
    [community, t],
  );

  const {
    dropdown,
    onClick: onJoinButtonClick,
    status,
    statusText,
  }: {
    dropdown?: MenuProps;
    onClick?: () => void;
    status: JoinButtonStatus;
    statusText?: string;
  } = useMemo(() => {
    // TODO maybe move this logic somewhere else
    if (
      community.organization.requiredConsents.some(
        (requiredConsent) => !viewer.usersConsents.some((consent) => requiredConsent.id === consent.id),
      )
    ) {
      return {
        onClick() {
          setIsOpened('consents');
        },
        status: 'join',
        statusText: 'Confirm consents to join',
      };
    }

    if (isRequestPending) {
      return {
        status: 'pending',
      };
    }

    if (isMemberInCommunity) {
      return {
        dropdown: canLeaveCommunity({ community })
          ? {
              items: [
                {
                  icon: <ExitLineIcon />,
                  key: 'leave',
                  label: t('Leave'),
                },
              ],
              onClick({ key }: { key: string }) {
                if (key === 'leave') {
                  modal.confirm({
                    content: 'Do you want to leave selected community?',
                    icon: <Delete2LineIcon type="error" />,
                    okText: 'Leave',
                    onOk() {
                      leaveCommunity({ id: community.id });
                    },
                    title: 'Leave community?',
                    type: 'error',
                  });
                }
              },
            }
          : undefined,
        status: 'joined',
      };
    }

    if (community.privacy === 'private') {
      return {
        onClick() {
          setIsOpened('request');
        },
        status: 'request',
      };
    }

    return {
      onClick() {
        joinCommunity({ id: community.id, privacy: community.privacy });
      },
      status: 'join',
    };
  }, [
    community,
    isRequestPending,
    isMemberInCommunity,
    viewer.usersConsents,
    canLeaveCommunity,
    t,
    modal,
    leaveCommunity,
    joinCommunity,
  ]);

  if (isJoinedSuccess && isMemberInCommunity) {
    // TODO: This should not be in feature
    // eslint-disable-next-line sonarjs/void-use -- We dont want to wait for this navigate
    void navigate(mapCommunityTabToPathName(community.id, '/feed'));
    return null;
  }

  return (
    <div className="community-header">
      <HeaderImage
        alt={t('Community cover image')}
        onRequestEdit={
          canManageCommunityProfile({ community, viewer })
            ? () => {
                setIsOpened('cover');
              }
            : undefined
        }
        src={community.coverPhoto.url}
      />
      <div className="community-header__content">
        <Row align="middle" className="community-header__content__info" wrap={false}>
          <Col>
            <Avatar photo={community.profilePhoto?.url ?? images.default.communityProfilePhoto} />
          </Col>
          <Col className="community-header__content__info__data" flex={1}>
            <Row>
              <Col span={24}>
                <Title className="community-header__content__info__title" level={3} marginBottom>
                  {community.name}
                </Title>
              </Col>
              <Col span={24}>
                <DotSeparator items={communityInfoItems} />
              </Col>
            </Row>
          </Col>
          <Col>
            <Row>
              <Col className="community-header__content__actions" span={24}>
                <JoinStatusButton
                  dropdown={dropdown}
                  loading={isReloadingCommunity || isJoiningCommunity || isLeavingCommunity}
                  onClick={onJoinButtonClick}
                  status={status}
                >
                  {statusText}
                </JoinStatusButton>
                {status === 'joined' && (
                  <Button
                    className="community-header__content__actions__notification"
                    icon={<NotificationLineIcon />}
                    onClick={() => {
                      setIsOpened('notifications');
                    }}
                    type="link-secondary"
                  />
                )}
              </Col>
            </Row>
          </Col>
        </Row>
        <CommunityTabs
          active={tab}
          communityId={community.id}
          isPublicView={!isMemberInCommunity}
          showMembers={canViewCommunityMembers({ community })}
        />
      </div>
      {isOpened === 'request' ? (
        <RequestJoinModal
          isOpened
          onCancel={() => {
            setIsOpened(undefined);
          }}
          onFinish={onRequestJoin}
        />
      ) : null}
      {isOpened === 'notifications' ? (
        <NotificationSettingsModal
          communityId={community.id}
          isOpened
          onClose={() => {
            setIsOpened(undefined);
          }}
        />
      ) : null}
      {isOpened === 'cover' ? (
        <EditImageModal
          image={community.coverPhoto}
          isLoading={isUpdatingCommunity}
          isOpened
          onCancel={() => {
            setIsOpened(undefined);
          }}
          onSubmit={onEditCoverImage}
          title="Edit Cover Image"
        />
      ) : null}
      {isOpened === 'consents' ? (
        <ConsentsModal
          entity={community}
          isOpened
          onAccept={() => {
            setIsOpened(undefined);
          }}
          onClose={() => {
            setIsOpened(undefined);
          }}
          onReject={() => {
            setIsOpened(undefined);
          }}
          requiredConsentIds={community.organization.requiredConsents.map((consent) => consent.id)}
        />
      ) : null}
      {contextHolder}
    </div>
  );
};

export default CommunityHeader;
